import { z } from 'zod';
import { DEFAULT_SUPPORT_PARAMS } from '../supports/generate';
import { DEFAULT_PRINT_SETTINGS, worldMetersToPrintMm } from '../print/types';
import { DEFAULT_TRACK_SHAPE } from '../geometry/gearRack';

export const FORMAT = 'rcsim';
export const CURRENT_VERSION = 1;

const vec3 = z.tuple([z.number(), z.number(), z.number()]);

const nodeSchema = z.object({
  id: z.string().min(1),
  p: vec3,
  hIn: vec3,
  hOut: vec3,
  mode: z.enum(['smooth', 'broken']).default('smooth'),
  /** When present, pin that channel of the node tangent (°) after autosmooth. */
  forcedYawDeg: z.number().optional(),
  forcedPitchDeg: z.number().optional(),
  /** True once the user has authored this node's world position. */
  forcedPos: z.boolean().optional(),
  /** When present, pin gravity-relative banking (°) at this node (0° = rails level with the ground). */
  forcedBankDeg: z.number().optional(),
});

const rollKeySchema = z.object({
  s: z.number().min(0),
  deg: z.number(),
  interp: z.enum(['smooth', 'linear']).default('smooth'),
});

const sectionSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('station'), from: z.number(), to: z.number() }),
  z.object({
    kind: z.literal('brake'),
    from: z.number(),
    to: z.number(),
    target: z.number().min(0),
    decel: z.number().positive(),
  }),
]);

export const coasterSchema = z.object({
  format: z.literal(FORMAT),
  version: z.number().int().min(1),
  meta: z.object({
    name: z.string().default('Untitled'),
    author: z.string().default(''),
    created: z.string().default(''),
  }),
  settings: z.object({
    gravity: z.number().positive().default(9.80665),
    friction: z.number().min(0).default(0.025),
    drag: z.number().min(0).default(0.002),
    minSpeed: z.number().positive().default(2.0),
  }),
  train: z.object({
    cars: z.number().int().min(1).max(12).default(5),
    carMass: z.number().positive().default(900),
    seatsPerCar: z.number().int().positive().default(4),
    carLength: z.number().positive().default(3.2),
  }),
  start: z.object({
    position: vec3.default([0, 0, 0]),
    heading: z.number().default(0),
  }),
  track: z.object({
    closed: z.boolean().default(false),
    nodes: z.array(nodeSchema),
    rollKeys: z.array(rollKeySchema).default([]),
    /** @deprecated Unused — inversions are authored with roll keys. Kept for file compat. */
    verticalInverts: z.array(z.number().min(0)).default([]),
    sections: z.array(sectionSchema).default([]),
  }),
  supports: z
    .object({
      supportCount: z.number().int().positive().default(DEFAULT_SUPPORT_PARAMS.supportCount),
      trackSpacing: z.number().positive().default(DEFAULT_SUPPORT_PARAMS.trackSpacing),
      maxSpan: z.number().positive().default(DEFAULT_SUPPORT_PARAMS.maxSpan),
      columnRadius: z.number().positive().default(DEFAULT_SUPPORT_PARAMS.columnRadius),
      braceRadius: z.number().positive().default(DEFAULT_SUPPORT_PARAMS.braceRadius),
      maxLeanDeg: z.number().min(0).default(DEFAULT_SUPPORT_PARAMS.maxLeanDeg),
      clearance: z.number().min(0).default(DEFAULT_SUPPORT_PARAMS.clearance),
      minTrackHeight: z.number().min(0).default(DEFAULT_SUPPORT_PARAMS.minTrackHeight),
      braceMinHeight: z.number().min(0).default(DEFAULT_SUPPORT_PARAMS.braceMinHeight),
      braceAngleDeg: z.number().min(10).max(80).default(DEFAULT_SUPPORT_PARAMS.braceAngleDeg),
      beamHeightGridOffset: z.number().default(DEFAULT_SUPPORT_PARAMS.beamHeightGridOffset),
      beamHeightGridStep: z.number().positive().default(DEFAULT_SUPPORT_PARAMS.beamHeightGridStep),
      attachDepth: z.number().positive().default(DEFAULT_SUPPORT_PARAMS.attachDepth),
      attachStub: z.number().min(0).default(DEFAULT_SUPPORT_PARAMS.attachStub),
      attachStubRadius: z.number().positive().default(DEFAULT_SUPPORT_PARAMS.attachStubRadius),
      triangleSpread: z.number().min(0).default(DEFAULT_SUPPORT_PARAMS.triangleSpread),
      triangleMinLeanDeg: z.number().min(0).max(90).default(DEFAULT_SUPPORT_PARAMS.triangleMinLeanDeg),
      triangleMinHeight: z.number().min(0).default(DEFAULT_SUPPORT_PARAMS.triangleMinHeight),
      braceMainOnly: z.boolean().default(DEFAULT_SUPPORT_PARAMS.braceMainOnly),
      footerMergeDistance: z.number().min(0).default(DEFAULT_SUPPORT_PARAMS.footerMergeDistance),
      allowTriangleMerge: z.boolean().default(DEFAULT_SUPPORT_PARAMS.allowTriangleMerge),
    })
    .default({ ...DEFAULT_SUPPORT_PARAMS }),
  supportsEnabled: z.boolean().default(true),
  supportOverrides: z
    .array(
      z.discriminatedUnion('kind', [
        z.object({ kind: z.literal('force'), s: z.number().min(0) }),
        z.object({
          kind: z.literal('suppress'),
          from: z.number().min(0),
          to: z.number().min(0),
        }),
        z.object({
          kind: z.literal('forceFoot'),
          s: z.number().min(0),
          x: z.number(),
          z: z.number(),
          role: z.enum(['main', 'triangle']).optional(),
        }),
        z.object({
          kind: z.literal('mergeFeet'),
          sA: z.number().min(0),
          roleA: z.enum(['main', 'triangle']).optional(),
          sB: z.number().min(0),
          roleB: z.enum(['main', 'triangle']).optional(),
          extra: z
            .array(
              z.object({
                s: z.number().min(0),
                role: z.enum(['main', 'triangle']).optional(),
              }),
            )
            .optional(),
        }),
      ]),
    )
    .default([]),
  trackShape: z
    .object({
      railDiameter: z.number().positive().default(DEFAULT_TRACK_SHAPE.railDiameter),
      railSpacing: z.number().positive().default(DEFAULT_TRACK_SHAPE.railSpacing),
      flatBeamHeight: z.number().positive().default(DEFAULT_TRACK_SHAPE.flatBeamHeight),
      triangleWidth: z.number().positive().default(DEFAULT_TRACK_SHAPE.triangleWidth),
      triangleHeight: z.number().positive().default(DEFAULT_TRACK_SHAPE.triangleHeight),
      trackType: z.enum(['type1', 'type2']).default(DEFAULT_TRACK_SHAPE.trackType),
      type2TopGap: z.number().positive().default(DEFAULT_TRACK_SHAPE.type2TopGap),
      type2BottomWidth: z.number().min(0).default(DEFAULT_TRACK_SHAPE.type2BottomWidth),
      type2VHeight: z.number().min(0).default(DEFAULT_TRACK_SHAPE.type2VHeight),
      type2RackTopWidth: z.number().min(0).default(DEFAULT_TRACK_SHAPE.type2RackTopWidth),
      type2RackTopHeight: z.number().min(0).default(DEFAULT_TRACK_SHAPE.type2RackTopHeight),
      type2RackBottomHeight: z.number().min(0).default(DEFAULT_TRACK_SHAPE.type2RackBottomHeight),
      type2BeamHeight: z.number().min(0).default(DEFAULT_TRACK_SHAPE.type2BeamHeight),
      type2CircleDiameter: z.number().positive().default(DEFAULT_TRACK_SHAPE.type2CircleDiameter),
      type2Thickness: z.number().positive().default(DEFAULT_TRACK_SHAPE.type2Thickness),
      type2SolidWebs: z.boolean().default(DEFAULT_TRACK_SHAPE.type2SolidWebs),
      type2PlateGap: z.number().positive().default(DEFAULT_TRACK_SHAPE.type2PlateGap),
      type2PlateThickness: z.number().positive().default(DEFAULT_TRACK_SHAPE.type2PlateThickness),
      enabled: z.boolean().default(DEFAULT_TRACK_SHAPE.enabled),
      pitch: z.number().positive().default(DEFAULT_TRACK_SHAPE.pitch),
      toothHeight: z.number().positive().default(DEFAULT_TRACK_SHAPE.toothHeight),
      toothWidth: z.number().positive().default(DEFAULT_TRACK_SHAPE.toothWidth),
      toothThickness: z.number().positive().default(DEFAULT_TRACK_SHAPE.toothThickness),
    })
    .default({ ...DEFAULT_TRACK_SHAPE }),
  print: z
    .object({
      chunkSizeMm: z
        .number()
        .min(80)
        .max(350)
        .default(DEFAULT_PRINT_SETTINGS.chunkSizeMm),
      supportCutClearanceMm: z
        .number()
        .min(0)
        .max(50)
        .default(DEFAULT_PRINT_SETTINGS.supportCutClearanceMm),
      separateSupport: z.boolean().default(DEFAULT_PRINT_SETTINGS.separateSupport),
      splitAtToothBottom: z.boolean().default(DEFAULT_PRINT_SETTINGS.splitAtToothBottom),
      hssEndplateEnabled: z.boolean().default(DEFAULT_PRINT_SETTINGS.hssEndplateEnabled),
      hssEndplateThicknessMm: z
        .number()
        .min(0.3)
        .max(3)
        .default(DEFAULT_PRINT_SETTINGS.hssEndplateThicknessMm),
      hssEndplateDiameterMm: z
        .number()
        .min(4)
        .max(40)
        .default(DEFAULT_PRINT_SETTINGS.hssEndplateDiameterMm),
      hssEndplateBoltCount: z
        .number()
        .int()
        .min(4)
        .max(12)
        .default(DEFAULT_PRINT_SETTINGS.hssEndplateBoltCount),
      hssEndplateBoltHoleMm: z
        .number()
        .min(0.3)
        .max(2)
        .default(DEFAULT_PRINT_SETTINGS.hssEndplateBoltHoleMm),
      hssEndplateBoltCircle: z
        .number()
        .min(0)
        .max(1)
        .default(DEFAULT_PRINT_SETTINGS.hssEndplateBoltCircle),
      maxBeamLengthMm: z
        .number()
        .min(0)
        .max(350)
        .default(DEFAULT_PRINT_SETTINGS.maxBeamLengthMm),
      hssSplitThicknessMm: z
        .number()
        .min(0.15)
        .max(3)
        .default(DEFAULT_PRINT_SETTINGS.hssSplitThicknessMm),
      hssSplitDiameterMm: z
        .number()
        .min(4)
        .max(40)
        .default(DEFAULT_PRINT_SETTINGS.hssSplitDiameterMm),
      hssSplitBoltCount: z
        .number()
        .int()
        .min(4)
        .max(12)
        .default(DEFAULT_PRINT_SETTINGS.hssSplitBoltCount),
      hssSplitBoltHoleMm: z
        .number()
        .min(0.15)
        .max(2)
        .default(DEFAULT_PRINT_SETTINGS.hssSplitBoltHoleMm),
      hssSplitBoltCircle: z
        .number()
        .min(0)
        .max(1)
        .default(DEFAULT_PRINT_SETTINGS.hssSplitBoltCircle),
      footerThicknessMm: z
        .number()
        .min(1.2)
        .max(8)
        .default(DEFAULT_PRINT_SETTINGS.footerThicknessMm),
      footerSizeMm: z
        .number()
        .min(5)
        .max(40)
        .default(DEFAULT_PRINT_SETTINGS.footerSizeMm),
      footerAttachStubMm: z
        .number()
        .min(0)
        .max(15)
        .default(DEFAULT_PRINT_SETTINGS.footerAttachStubMm),
      groundPlaneEnabled: z.boolean().default(DEFAULT_PRINT_SETTINGS.groundPlaneEnabled),
      groundPlaneHeightMm: z
        .number()
        .min(0.8)
        .max(8)
        .default(DEFAULT_PRINT_SETTINGS.groundPlaneHeightMm),
      groundButterflyDepthMm: z
        .number()
        .min(0.4)
        .max(8)
        .default(DEFAULT_PRINT_SETTINGS.groundButterflyDepthMm),
    })
    .default({ ...DEFAULT_PRINT_SETTINGS }),
});

export type CoasterFile = z.infer<typeof coasterSchema>;

/** Strip legacy chain-lift sections and rename chainSpeed → minSpeed before zod. */
function migrateLegacy(data: Record<string, unknown>): void {
  const settings = data.settings as Record<string, unknown> | undefined;
  if (settings && typeof settings === 'object') {
    if (settings.minSpeed == null && typeof settings.chainSpeed === 'number') {
      settings.minSpeed = settings.chainSpeed;
    }
    delete settings.chainSpeed;
  }
  const track = data.track as { sections?: unknown[] } | undefined;
  if (track && Array.isArray(track.sections)) {
    let chainSpeed: number | undefined;
    track.sections = track.sections.filter((sec) => {
      if (sec && typeof sec === 'object' && (sec as { kind?: string }).kind === 'chain') {
        const speed = (sec as { speed?: number }).speed;
        if (typeof speed === 'number' && speed > 0) chainSpeed = speed;
        return false;
      }
      return true;
    });
    if (chainSpeed != null && settings && typeof settings === 'object' && settings.minSpeed == null) {
      settings.minSpeed = chainSpeed;
    }
  }
  const supports = data.supports as Record<string, unknown> | undefined;
  if (supports && typeof supports === 'object') {
    // Drop legacy metre-spacing knobs; supportCount defaults via zod when absent.
    delete supports.spacing;
    delete supports.xySpacing;
  }
  // Legacy gearRack block → trackShape (adds railDiameter default via zod).
  if (data.trackShape == null && data.gearRack != null && typeof data.gearRack === 'object') {
    data.trackShape = data.gearRack;
  }
  delete data.gearRack;
  const trackShape = data.trackShape as Record<string, unknown> | undefined;
  if (trackShape && typeof trackShape === 'object') {
    if (trackShape.flatBeamHeight == null && typeof trackShape.flatBeamWidth === 'number') {
      trackShape.flatBeamHeight = trackShape.flatBeamWidth;
    }
    delete trackShape.flatBeamWidth;
    // Predates the rack-top width slider: the rack top was flush with the stem.
    if (trackShape.type2RackTopWidth == null && typeof trackShape.type2BottomWidth === 'number') {
      trackShape.type2RackTopWidth = trackShape.type2BottomWidth;
    }
    // Predates the plate thickness slider: plates were as thick as the V lines.
    if (trackShape.type2PlateThickness == null && typeof trackShape.type2Thickness === 'number') {
      trackShape.type2PlateThickness = trackShape.type2Thickness;
    }
  }
  const print = data.print as Record<string, unknown> | undefined;
  if (print && typeof print === 'object') {
    // Legacy plan-size multiplier → absolute square side in print mm.
    if (print.footerSizeMm == null && typeof print.footerSizeScale === 'number') {
      const baseHalfM = 0.22 * 2.2;
      print.footerSizeMm = 2 * worldMetersToPrintMm(baseHalfM) * print.footerSizeScale;
    }
    delete print.footerSizeScale;
    // Legacy HSS overhang × sizeScale → absolute diameter (typical 0.22 m column).
    if (print.hssEndplateDiameterMm == null && typeof print.hssEndplateOverhangMm === 'number') {
      const scale =
        typeof print.hssEndplateSizeScale === 'number'
          ? Math.max(print.hssEndplateSizeScale, 0.01)
          : 1;
      print.hssEndplateDiameterMm =
        2 * (worldMetersToPrintMm(0.22) + print.hssEndplateOverhangMm * scale);
    }
    delete print.hssEndplateOverhangMm;
    delete print.hssEndplateSizeScale;
    // Legacy beam-split size multiplier → absolute HSSS knobs.
    if (typeof print.beamSplitHssScale === 'number') {
      const scale = Math.max(print.beamSplitHssScale, 0.01);
      const baseThick =
        typeof print.hssEndplateThicknessMm === 'number' ? print.hssEndplateThicknessMm : 0.8;
      const baseHole =
        typeof print.hssEndplateBoltHoleMm === 'number' ? print.hssEndplateBoltHoleMm : 0.6;
      if (print.hssSplitThicknessMm == null) print.hssSplitThicknessMm = baseThick * scale;
      if (print.hssSplitBoltHoleMm == null) print.hssSplitBoltHoleMm = baseHole * scale;
      if (print.hssSplitDiameterMm == null) print.hssSplitDiameterMm = 9.5 * (scale / 0.5);
      if (print.hssSplitBoltCount == null && typeof print.hssEndplateBoltCount === 'number') {
        print.hssSplitBoltCount = print.hssEndplateBoltCount;
      }
      if (print.hssSplitBoltCircle == null && typeof print.hssEndplateBoltCircle === 'number') {
        print.hssSplitBoltCircle = print.hssEndplateBoltCircle;
      }
    }
    delete print.beamSplitHssScale;
  }
}

/** Validate + migrate an untrusted parsed JSON value into the current format. */
export function parseCoasterFile(data: unknown): CoasterFile {
  const obj = data as Record<string, unknown>;
  if (obj && typeof obj === 'object') {
    if (typeof obj.version === 'number' && obj.version < CURRENT_VERSION) {
      // future migrations go here, oldest first
    }
    migrateLegacy(obj);
  }
  return coasterSchema.parse(data);
}
